/**
 * 配信予約管理ページ。
 * 仕様書: docs/01_spec/spec_mail_system.md B-12.4
 */
import {
  AlertCircle,
  Ban,
  CalendarClock,
  CheckCircle2,
  Clock,
  Mail as MailIcon,
  Send,
  Tag,
  Trash2,
  Users,
  XCircle,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useMailGroups } from '@/hooks/useMailGroups';
import {
  useCancelScheduledMail,
  useDeleteScheduledMail,
  useMailSchedules,
} from '@/hooks/useMailSchedules';
import { formatDateTime } from '@/lib/firestoreTime';

import { ScheduleFormDialog } from './ScheduleFormDialog';

import type { MailScheduleStatus } from '@goenhub/shared';

const STATUS_LABEL: Record<MailScheduleStatus, string> = {
  scheduled: '予約済',
  sending: '送信中',
  sent: '送信完了',
  failed: '失敗',
  canceled: 'キャンセル',
};

const STATUS_BADGE: Record<MailScheduleStatus, 'draft' | 'review' | 'approved' | 'sent' | 'danger' | 'default'> = {
  scheduled: 'review',
  sending: 'approved',
  sent: 'sent',
  failed: 'danger',
  canceled: 'default',
};

const STATUS_ICON: Record<MailScheduleStatus, typeof Clock> = {
  scheduled: Clock,
  sending: Send,
  sent: CheckCircle2,
  failed: AlertCircle,
  canceled: XCircle,
};

export function MailSchedulesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const filterMailId = queryParams.get('mailId') ?? undefined;

  const [statusFilter, setStatusFilter] = useState<MailScheduleStatus | ''>('');
  const [formOpen, setFormOpen] = useState(false);
  const { data: schedules, isLoading } = useMailSchedules({
    status: statusFilter || undefined,
    mailId: filterMailId,
  });
  const { data: groups } = useMailGroups();
  const groupMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of groups ?? []) m.set(g.id, g.name);
    return m;
  }, [groups]);
  const cancelMutation = useCancelScheduledMail();
  const deleteMutation = useDeleteScheduledMail();

  const handleCancel = async (id: string, subject: string) => {
    if (!window.confirm(`「${subject}」の予約をキャンセルします。よろしいですか？`)) return;
    try {
      await cancelMutation.mutateAsync(id);
      toast.success('予約をキャンセルしました');
    } catch (err) {
      toast.error(`キャンセル失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleDelete = async (
    id: string,
    subject: string,
    status: MailScheduleStatus,
  ) => {
    const message =
      status === 'sending'
        ? `「${subject}」は送信中です。削除すると未送信分の送信が停止し、リストから完全に削除されます。元に戻せません。よろしいですか？`
        : `「${subject}」の予約レコードを完全に削除します（元に戻せません）。よろしいですか？`;
    if (!window.confirm(message)) return;
    try {
      await deleteMutation.mutateAsync(id);
      toast.success('予約レコードを削除しました');
    } catch (err) {
      toast.error(`削除失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const targetLabel = (s: { targetType: string; targetIds: string[] }): string => {
    if (s.targetType === 'group') {
      const gid = s.targetIds[0];
      return `グループ: ${groupMap.get(gid) ?? '(削除済)'}`;
    }
    if (s.targetType === 'tag') {
      return `タグ: ${s.targetIds.join(', ')}`;
    }
    return `アドレス指定: ${s.targetIds.length} 件`;
  };

  return (
    <Screen
      title="配信予約"
      description={
        filterMailId
          ? '指定メールの予約のみ表示中（クエリで絞り込み）'
          : 'メール送信を予約・キャンセル・実行状況を管理'
      }
      actions={
        <>
          {filterMailId && (
            <Button type="button" variant="ghost" onClick={() => navigate('/mail/schedules')}>
              絞り込み解除
            </Button>
          )}
          <Button type="button" variant="primary" onClick={() => setFormOpen(true)}>
            <CalendarClock className="size-4 mr-1" />
            新規配信予約
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* ステータスフィルタ */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-[var(--text-muted)]">ステータス:</span>
          {(['', 'scheduled', 'sending', 'sent', 'failed', 'canceled'] as const).map((s) => (
            <button
              key={s || 'all'}
              type="button"
              onClick={() => setStatusFilter(s as MailScheduleStatus | '')}
              className={
                'px-3 py-1 rounded-sm text-xs border transition-colors ' +
                (statusFilter === s
                  ? 'border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--accent)]'
                  : 'border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--text-muted)]')
              }
            >
              {s ? STATUS_LABEL[s as MailScheduleStatus] : 'すべて'}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center p-12">
            <Spinner />
          </div>
        ) : !schedules || schedules.length === 0 ? (
          <EmptyState
            icon={CalendarClock}
            title="予約がまだありません"
            description="右上の「新規配信予約」から作成できます"
          />
        ) : (
          <div className="space-y-2">
            {schedules.map((s) => {
              const StatusIcon = STATUS_ICON[s.status];
              const TargetIcon =
                s.targetType === 'group' ? Users : s.targetType === 'tag' ? Tag : MailIcon;
              return (
                <div
                  key={s.id}
                  className="rounded bg-[var(--bg-elevated)] border border-[var(--border)] p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant={STATUS_BADGE[s.status]}>
                          <StatusIcon className="size-3 mr-1 inline" />
                          {STATUS_LABEL[s.status]}
                        </Badge>
                        <span className="text-[13px] font-medium truncate">{s.subject}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-[var(--text-secondary)] mt-2">
                        <div className="flex items-center gap-1.5">
                          <CalendarClock className="size-3 text-[var(--text-muted)]" />
                          <span>{formatDateTime(s.sendAt)}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <TargetIcon className="size-3 text-[var(--text-muted)]" />
                          <span className="truncate">{targetLabel(s)}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Send className="size-3 text-[var(--text-muted)]" />
                          <span>
                            {s.recipientCount} 件
                            {s.sentRecipientCount !== undefined && (
                              <span className="text-[var(--text-muted)] ml-1">
                                （送信済 {s.sentRecipientCount}
                                {!!s.failedRecipientCount && s.failedRecipientCount > 0 && (
                                  <span className="text-[var(--accent-red)]">
                                    {' '}/ 失敗 {s.failedRecipientCount}
                                  </span>
                                )}
                                ）
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <MailIcon className="size-3 text-[var(--text-muted)]" />
                          <span className="font-mono text-[10px] truncate">{s.fromAddress}</span>
                        </div>
                      </div>
                      {s.lastError && (
                        <div className="mt-2 text-[11px] text-[var(--accent-red)] bg-[var(--accent-red-subtle)] rounded px-2 py-1">
                          ⚠ {s.lastError}（試行 {s.attempts} 回）
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                      {s.status === 'scheduled' && (
                        <Button
                          type="button"
                          variant="danger"
                          size="sm"
                          onClick={() => handleCancel(s.id, s.subject)}
                          disabled={cancelMutation.isPending}
                        >
                          <Ban className="size-3 mr-1" />
                          キャンセル
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(s.id, s.subject, s.status)}
                        disabled={deleteMutation.isPending}
                        title="この予約レコードを削除"
                        className="text-[var(--accent-red)] hover:bg-[var(--accent-red-subtle)]"
                      >
                        <Trash2 className="size-3 mr-1" />
                        削除
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ScheduleFormDialog open={formOpen} onOpenChange={setFormOpen} />
    </Screen>
  );
}
